import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { localize } from 'react-localize-redux';
import styled from 'styled-components';
import { useTransition, animated } from 'react-spring';
import moment from 'moment';

import { toFriendlyDateString } from 'helpers/Formatter';
import { getTranslatedString, getTranslatedName } from 'helpers/i18n';
import { RentRollStatus, ParcelStatus } from 'components/shared/StatusHelper';
import RenderInBatches from 'components/ui/RenderInBatches';
import StatusTag from 'components/ui/StatusTag';
import PriorityIcon from 'components/ui/PriorityIcon';
import CtaCard from 'components/ui/CtaCard';
import { groupListByKey } from 'helpers/tableHelpers';
import groupSvg from 'assets/group-2.svg';
import loadingGif from 'assets/yuhu-loading.gif';
import { formatMoney } from 'helpers/Formatter';
import { isIOS, isIOS13, isMobileSafari, isIPad13 } from 'react-device-detect';
import MobileNavContainer from 'components/ui/MobileNavDropdown/MobileNavContainer';

const StyledCtaCard = styled(CtaCard)`
  margin: ${props => (props.expand ? 'auto' : 'inherit')};
`;

const StyledContainer = styled.div`
  width: 100%;
  padding-bottom: ${props => (props.isMobile ? '115px' : '20px')};
  padding-top: ${props => (props.hide ? '0px' : '20px')};
`;

const StyledRowContainer = styled.div`
  box-shadow: var(--box-shadow);
  margin: 0 20px 30px;
  padding: 20px 20px 0px;
  position: relative;
  border-radius: 8px;
`;

const StyledTableHeader = styled.div`
  width: 100%;
  height: 50px;
  line-height: 50px;
  font-size: 18px;
  font-weight: bold;
  background-color: #fff;
  color: var(--table-title-text);
  margin-bottom: 24px;
  position: sticky;
  z-index: 1;
  top: ${props => props.stickyHeaderTop || '76px'};
  padding-left: 24px;
  font-family: 'Circular-Black', 'Circular', sans-serif;
  &:after {
    display: block;
    content: '';
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: 100vw;
    height: 1px;
    background-color: var(--light-periwinkle);
  }
`;

const StyledRowField = styled.div`
  font-size: 14px;
  font-weight: bold;
  font-family: 'Circular-Bold', 'Circluar', sans-serif; /* TODO: should expose this as API */
  color: var(--dark);
  width: ${props => (props.half ? `50%` : 'auto')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 26px;
  ${props =>
    props.half
      ? `
    &:nth-child(odd) {
      padding-right: 10px;
    }
    &:nth-child(even) {
      padding-left: 20px;
      border-left: 1px solid var(--light-periwinkle);
    }
  `
      : null};
`;

const StyledSpacer = styled.div`
  padding-top: ${props => (props.hide ? '0' : '2rem')};
  background-color: white;
`;

const StyledRowFieldName = styled.div`
  color: var(--bluey-grey);
  margin-bottom: 5px;
  font-size: 14px;
  font-weight: normal;
`;

const StyledCtaContainer = styled.div`
  border-top: 1px solid var(--light-periwinkle);
  display: flex;
  padding: 16px 0px;
  justify-content: space-between;
`;

const StyledRowsBelowFold = styled(animated.div)`
  border-top-color: var(--light-periwinkle);
  border-top-style: solid;
  overflow: hidden;
`;

const StyledRowHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledRowTitle = styled.div`
  font-family: 'Circluar', sans-serif;
  font-size: 18px;
  margin-top: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  height: 27px;
`;

const StyledRowBody = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 24px;
`;

const StyledPriorityIcon = styled(PriorityIcon)`
  margin-right: 4px;
  line-height: 21px !important;
  vertical-align: middle !important;
`;

const StyledImgContainer = styled.div`
  display: ${props => (props.shouldRender ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;
  margin-top: 24px;
`;

const StyledIllustration = styled.img`
  width: 80%;
  max-width: 576px;
  margin-bottom: 24px;
`;

const StyledLoader = styled.img`
  width: 200px;
`;

const StyledTextNoMatch = styled.p`
  width: 100%;
  max-width: 269px;
  font-family: 'Circular-Bold', 'Circluar', sans-serif;
  font-size: 16px;
  text-align: center;
`;

const Field = ({ title, renderer, label, data, translate, currentLanguage, dataType, ...rest }) => {
  let text;
  if (
    [
      'last_active',
      'scheduled_starts_at',
      'request_date',
      'last_updated',
      'post_date',
      'expiration_date',
      'last_sign_in_at',
    ].includes(label)
  ) {
    const date = moment(data);
    if (date.isValid()) {
      text = date.locale(currentLanguage || 'en').fromNow();
    } else if (label === 'scheduled_starts_at') {
      text = translate('landlordPortal.mobileTable.notScheduled').props.children;
    } else {
      text = '-';
    }
  } else if (['due_at', 'created_at', 'required_date'].includes(label)) {
    text = toFriendlyDateString(data) || '-';
  } else if (dataType === 'money') {
    text = data ? formatMoney(data) : '';
  } else if (dataType === 'boolean') {
    text = data ? getTranslatedString('request_type.yes') : getTranslatedString('request_type.no');
  } else {
    text = data || '-';
  }

  let value;
  if (renderer) {
    value = renderer(data);
  } else {
    value = (
      <div>
        {label === 'priority' ? <StyledPriorityIcon priority={data} /> : null}
        {text}
      </div>
    );
  }
  return (
    <StyledRowField {...rest}>
      <StyledRowFieldName>{title}</StyledRowFieldName>
      {value}
    </StyledRowField>
  );
};

const Row = ({
  rowData,
  title,
  tag,
  tagType,
  hideOptionsIfBlank,
  rowHeader,
  fieldsAboveFold,
  fieldsBelowFold,
  onRowClick,
  stickyHeaderTop,
  currentLanguage,
  translate,
}) => {
  const [showMore, setShowMore] = React.useState(false);
  const transitions = useTransition(showMore, null, {
    from: {
      paddingTop: 0,
      borderTopWidth: 0,
      height: 0,
    },
    leave: {
      paddingTop: 0,
      borderTopWidth: 0,
      height: 0,
    },
    enter: {
      paddingTop: 24,
      borderTopWidth: 1,
      height: 73 * fieldsBelowFold.length + 24,
    },
  });

  const handleButtonClick = () => {
    setShowMore(!showMore);
  };

  if (rowData.tableHeader) {
    return (
      <StyledTableHeader stickyHeaderTop={stickyHeaderTop}>{rowData.tableHeader}</StyledTableHeader>
    );
  } else {
    let mobileTableTitle = rowData[title];
    if (mobileTableTitle instanceof Array) {
      mobileTableTitle = mobileTableTitle.join(', ');
    }

    return (
      <StyledRowContainer>
        <StyledRowHeader>
          {rowHeader ? (
            <StyledRowFieldName>{`${rowHeader === 'ticket_number' ? '#' : ''} ${getTranslatedName(
              rowData[rowHeader]
            )}`}</StyledRowFieldName>
          ) : null}
          {tag && !['parcels', 'rentRoll'].includes(tagType) ? (
            <StatusTag status={rowData[tag]} isMobile={true} />
          ) : null}
          {tag && tagType === 'rentRoll' ? (
            <RentRollStatus
              leaseDetails={rowData}
              isMobile={true}
              className="ticket_status_label_rent_roll"
            />
          ) : null}
          {tag && tagType === 'parcels' ? <ParcelStatus parcelDetails={rowData} isMobile /> : null}
        </StyledRowHeader>
        <div>
          <StyledRowTitle>{mobileTableTitle}</StyledRowTitle>
          <StyledRowBody>
            {fieldsAboveFold
              .filter(fields => !fields.hide)
              .map(field => (
                <Field
                  key={`rowfield-${field.key}`}
                  title={field.title}
                  label={field.key}
                  renderer={field.renderer}
                  data={rowData[field.key]}
                  dataType={field.dataType}
                  currentLanguage={currentLanguage}
                  half
                  translate={translate}
                />
              ))}
          </StyledRowBody>
        </div>
        {transitions.map(({ item, props, key }) => {
          if (item) {
            return (
              <StyledRowsBelowFold key={key} style={props}>
                {fieldsBelowFold
                  .filter(fields => !fields.hide)
                  .map(field => (
                    <Field
                      key={`rowfield-${field.key}`}
                      title={field.title}
                      renderer={field.renderer}
                      label={field.key}
                      dataType={field.dataType}
                      data={rowData[field.key]}
                      currentLanguage={currentLanguage}
                      translate={translate}
                    />
                  ))}
              </StyledRowsBelowFold>
            );
          } else {
            return null;
          }
        })}
        {hideOptionsIfBlank && !rowData[hideOptionsIfBlank] ? null : (
          <StyledCtaContainer>
            <StyledCtaCard expand={!onRowClick} onClick={handleButtonClick}>
              {showMore
                ? translate('landlordPortal.mobileTable.collapse')
                : translate('landlordPortal.mobileTable.expand')}
            </StyledCtaCard>
            {onRowClick && (
              <CtaCard onClick={() => onRowClick(rowData)}>
                {translate('landlordPortal.mobileTable.viewDetails')}
              </CtaCard>
            )}
          </StyledCtaContainer>
        )}
      </StyledRowContainer>
    );
  }
};

const availableTabs = (tabs, filtersActive) => {
  if (filtersActive) {
    return null;
  }
  return tabs;
};

const MobileTable = ({
  data,
  groupRowsBy,
  isLoading,
  currentLanguage,
  translate,
  emptyTableText,
  filtersActive,
  tabs,
  tabIndex,
  tabChange,
  ...rest
}) => {
  let rows;
  if (groupRowsBy) {
    [rows] = groupListByKey(data, groupRowsBy);
  } else {
    rows = data;
  }

  return (
    <StyledContainer isMobile={isIOS || isIOS13 || isIPad13 || isMobileSafari} hide={filtersActive}>
      <StyledSpacer hide={filtersActive} />
      <MobileNavContainer
        tabIndex={tabIndex}
        tabs={availableTabs(tabs, filtersActive)}
        disabled={filtersActive}
        onChange={tabChange}
      />
      <RenderInBatches first={20} by={200}>
        {rows.map(rowData => (
          <Row
            rowData={rowData}
            key={`row-${rowData.id || rowData.tableHeader}`}
            currentLanguage={currentLanguage}
            translate={translate}
            {...rest}
          />
        ))}
        <StyledImgContainer shouldRender={isLoading}>
          <StyledLoader src={loadingGif} alt="loader" />
        </StyledImgContainer>
        <StyledImgContainer shouldRender={!isLoading && rows.length === 0}>
          {emptyTableText ? (
            <StyledTextNoMatch>{emptyTableText}</StyledTextNoMatch>
          ) : (
            <Fragment>
              <StyledIllustration src={groupSvg} alt="no results found" />
              <StyledTextNoMatch>
                {translate('landlordPortal.mobileTable.hmmNoMatchToSearch')}
              </StyledTextNoMatch>
            </Fragment>
          )}
        </StyledImgContainer>
      </RenderInBatches>
    </StyledContainer>
  );
};

export default connect(store => ({
  currentLanguage: store.locale.currentLanguage,
}))(localize()(MobileTable));
